/**
 * TableauImportSummary — body of the post-import dialog shown after a
 * user uploads a Tableau .twb / .twbx / .tds / .tdsx file.
 *
 * Two main jobs:
 *   1. Tell the user what was created (datasource / column / measure / calc counts)
 *   2. Offer a one-click "Rebuild as INZARA dashboards" CTA that creates
 *      empty dashboard shells from the Tableau worksheet + dashboard names.
 *      This turns the import from a one-off schema dump into a tracked
 *      migration checklist — the user can walk down the list rebuilding
 *      each visual against the imported semantic model.
 */

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Database, LayoutGrid, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

export function TableauImportSummary({ result, onClose }) {
  const [scaffolding, setScaffolding] = useState(false);
  const [scaffolded, setScaffolded] = useState(null); // { created: [...] }

  const sourceNames = [
    ...(result.skipped?.worksheets || []).map(n => ({ name: n, kind: 'worksheet' })),
    ...(result.skipped?.dashboards || []).map(d => ({ name: d?.name || d, kind: 'dashboard' })),
  ].filter(x => x.name);

  const handleScaffold = async () => {
    setScaffolding(true);
    try {
      // CSRF from cookie — same pattern the rest of the app uses
      const csrf = (document.cookie.match(/(?:^|; )csrf_token=([^;]+)/) || [])[1] || '';
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/semantic/tableau-scaffold-dashboards`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': decodeURIComponent(csrf),
        },
        body: JSON.stringify({
          model_id: result.model?.model_id,
          names: sourceNames.map(s => s.name),
          kind: 'dashboard',
        }),
      });
      if (!resp.ok) throw new Error(await resp.text());
      const out = await resp.json();
      setScaffolded(out);
      toast.success(`Created ${out.count} dashboard shell(s)`);
    } catch (err) {
      toast.error(err.message || 'Failed to scaffold dashboards');
    } finally {
      setScaffolding(false);
    }
  };

  return (
    <div className="space-y-3 mt-2">
      <p className="text-xs text-muted-foreground">
        Created <span className="font-semibold text-foreground">{result.model?.name}</span>
        {result.summary?.tableau_build && <span> (Tableau build {result.summary.tableau_build})</span>}
      </p>
      <div className="grid grid-cols-2 gap-2">
        {[
          ['Datasources',       result.summary?.datasources_imported || 0,        'orange'],
          ['Columns',           result.summary?.columns_imported || 0,            'blue'],
          ['Measures',          result.summary?.measures_imported || 0,           'emerald'],
          ['Calculated fields', result.summary?.calculated_fields_imported || 0,  'amber'],
        ].map(([label, n, color]) => (
          <div key={label} className={`p-2 rounded-lg bg-${color}-500/10 border border-${color}-500/20`}>
            <div className={`text-base font-heading font-semibold text-${color}-400`}>{n}</div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</div>
          </div>
        ))}
      </div>
      {(result.summary?.calculated_fields_imported || 0) > 0 && (
        <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-[10px] text-amber-300 leading-relaxed">
          <strong>Note:</strong> Tableau calculated-field formulas were imported verbatim. INZARA&apos;s calculation DSL is different — open each calculated measure and rewrite using INZARA syntax.
        </div>
      )}

      {/* Migration CTA — rebuild Tableau worksheets / dashboards as INZARA shells */}
      {sourceNames.length > 0 && !scaffolded && (
        <div className="p-3 rounded-xl bg-primary/5 border border-primary/20 space-y-2">
          <div className="flex items-start gap-2">
            <LayoutGrid className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-xs font-medium text-foreground">Rebuild as INZARA Dashboards</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                Create <strong className="text-foreground">{sourceNames.length}</strong> empty dashboard shells from your Tableau {(result.skipped?.worksheets?.length || 0) > 0 && 'worksheets'}{((result.skipped?.worksheets?.length || 0) > 0 && (result.skipped?.dashboards?.length || 0) > 0) && ' & '}{(result.skipped?.dashboards?.length || 0) > 0 && 'dashboards'}. Each links to the new semantic model so you have a tracked migration checklist.
              </p>
            </div>
          </div>
          <Button
            onClick={handleScaffold}
            disabled={scaffolding}
            size="sm"
            className="w-full rounded-lg"
            data-testid="scaffold-tableau-dashboards-btn"
          >
            {scaffolding ? <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Creating shells…</> : <><LayoutGrid className="w-3.5 h-3.5 mr-2" /> Create {sourceNames.length} dashboard shell(s)</>}
          </Button>
        </div>
      )}

      {scaffolded && (
        <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 space-y-2" data-testid="scaffold-result">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            <p className="text-xs font-medium text-emerald-300">{scaffolded.count} dashboard shell(s) created</p>
          </div>
          <div className="max-h-32 overflow-y-auto space-y-1">
            {scaffolded.created.map(c => (
              <button
                key={c.dashboard_id}
                onClick={() => { onClose?.(); window.location.href = `/dashboard/${c.dashboard_id}`; }}
                className="w-full text-left text-[10px] px-2 py-1 rounded bg-muted/20 hover:bg-muted/40 text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5"
              >
                <Database className="w-3 h-3 text-emerald-400/70" />
                {c.name}
              </button>
            ))}
          </div>
          <p className="text-[9px] text-emerald-400/60 leading-relaxed">
            Open each shell and use the dashboard designer to recreate the Tableau visual against the imported semantic model.
          </p>
        </div>
      )}

      {sourceNames.length === 0 && (
        <div className="p-2 rounded-lg bg-muted/20 border border-border/30 text-[10px] text-muted-foreground leading-relaxed">
          No worksheets or dashboards detected in the Tableau file — only the data model was imported.
        </div>
      )}
    </div>
  );
}

export default TableauImportSummary;
